import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApPopover,
} from '@alixpartners/ui-components';
import { ShowMoreComponent } from 'Details/SpendByCategoryChart/ShowMoreComponent';
import { MiniChartCellRenderer } from './MiniChartCellRenderer';
import { StatusCellRenderer } from './StatusCellRenderer';
import { IconCellRenderer } from './IconCellRenderer';
import { TooltipContent } from './TooltipContent';
import { Legend } from '../Legend';
import { PAGE_SIZE } from './utils';
import {
	Wrapper,
	TableWrapper,
	ShowMoreWrapper,
	NoData,
	LoaderWrapper,
} from './Table.styles';

const HEADER_HEIGHT = 42;
const ROW_HEIGHT = 32;

export class Table extends React.Component {
	state = { tooltipData: null };
	gridApi = null;

	componentDidUpdate(prevProps) {
		const { isLoading } = this.props;

		if (isLoading !== prevProps.isLoading) {
			if (isLoading) {
				this.showLoadingOverlay();
			} else {
				this.hideLoadingOverlay();
			}
		}
	}

	getGridOptions = () => {
		const { data, columnDefs } = this.props;

		return {
			defaultColDef: {
				suppressMenu: true,
				// Prevent default sorting, as we handle sorting on server side
				comparator: () => 0,
				sortable: true,
			},
			components: {
				miniChartCellRenderer: MiniChartCellRenderer,
				statusCellRenderer: StatusCellRenderer,
				iconCellRenderer: IconCellRenderer,
			},
			rowData: data,
			rowHeight: ROW_HEIGHT,
			headerHeight: HEADER_HEIGHT,
			columnDefs,
			suppressCopyRowsToClipboard: true,
			suppressScrollOnNewData: true,
			suppressRowDrag: true,
			suppressRowClickSelection: true,
			suppressCellSelection: true,
			suppressCsvExport: true,
			suppressExcelExport: true,
			suppressContextMenu: true,
			suppressMultiSort: true,
			applyColumnDefOrder: true,
			domLayout: 'autoHeight',
			onGridReady: this.onGridReady,
			onSortChanged: this.handleSortChange,
			onCellClicked: this.handleCellClicked,
		};
	};

	onGridReady = params => {
		this.gridApi = params.api;
	};

	handleSortChange = () => {
		const { onSortChanged } = this.props;

		if (!this.gridApi) {
			return;
		}

		this.gridApi.sizeColumnsToFit();
		onSortChanged(this.gridApi.getSortModel());
	};

	showLoadingOverlay = () => {
		if (!this.gridApi) {
			return;
		}

		this.gridApi.showLoadingOverlay();
	};

	hideLoadingOverlay = () => {
		if (!this.gridApi) {
			return;
		}

		this.gridApi.hideOverlay();
	};

	handleCellClicked = e => {
		const { column } = e;

		if (column.colId !== 'tooltip') {
			return;
		}

		this.setState({ tooltipData: e });
	};

	hideTooltip = () => {
		this.setState({ tooltipData: null });
	};

	render() {
		const {
			data,
			loadMore,
			isLoading,
			totalCount,
			onShowLess,
			spendFormatter,
		} = this.props;
		const { tooltipData } = this.state;

		return (
			<Wrapper onMouseLeave={this.hideContextualTrigger}>
				{isLoading && data.length === 0 ? (
					<LoaderWrapper>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderWrapper>
				) : data.length === 0 ? (
					<NoData>No data available</NoData>
				) : (
					<React.Fragment>
						<Legend />
						<TableWrapper>
							<div className="ag-theme-balham">
								<AgGridReact {...this.getGridOptions()} />
							</div>
						</TableWrapper>
						<ShowMoreWrapper>
							<ShowMoreComponent
								shouldShowMore
								showMoreData={data.length < totalCount}
								showLessData={data.length > PAGE_SIZE}
								count={data.length}
								totalCount={totalCount}
								showMoreHandler={loadMore}
								showLessHandler={onShowLess}
							/>
						</ShowMoreWrapper>
						{tooltipData && (
							<ApPopover
								referenceElement={tooltipData.event && tooltipData.event.target}
								onClose={this.hideTooltip}
								placement="bottom"
								isOpen={true}
								modifiers={{
									preventOverflow: {
										enabled: true,
										boundariesElement: 'window',
									},
								}}
							>
								<TooltipContent
									data={tooltipData.data}
									spendFormatter={spendFormatter}
								/>
							</ApPopover>
						)}
					</React.Fragment>
				)}
			</Wrapper>
		);
	}
}
